import React, { useState } from 'react';
import { ShieldCheck, X, Check, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface ESignatureModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSign: (signatureData: ESignatureData) => void;
    documentTitle: string;
}

export interface ESignatureData {
    signerId: string;
    signerName: string;
    signerEmail: string;
    timestamp: string;
    signatureId: string;
    complianceText: string;
}

export const ESignatureModal: React.FC<ESignatureModalProps> = ({
    isOpen,
    onClose,
    onSign,
    documentTitle
}) => {
    const { user } = useAuth();
    const [typedName, setTypedName] = useState(user?.displayName || '');
    const [acknowledged, setAcknowledged] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!isOpen) return null;

    const handleSign = () => {
        if (!acknowledged) {
            setError('Please acknowledge the legal statement');
            return;
        }
        if (typedName.trim() === '') {
            setError('Please type your full name');
            return;
        }

        const signatureId = `SIG-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
        const timestamp = new Date().toISOString();
        const complianceText = "By electronically signing this document, I certify that I have reviewed the contents and that, to the best of my knowledge, the information provided is accurate and complete in accordance with enterprise quality standards (IATF 16949 / ISO 9001).";

        onSign({
            signerId: user?.uid || 'unknown',
            signerName: typedName,
            signerEmail: user?.email || 'unknown',
            timestamp,
            signatureId,
            complianceText
        });

        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="bg-slate-900 px-6 py-4 flex items-center justify-between text-white border-b border-slate-800">
                    <div className="flex items-center gap-2 font-bold">
                        <Lock className="w-4 h-4 text-emerald-400" />
                        Electronic Signature
                    </div>
                    <button onClick={onClose} className="hover:text-slate-300 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6">
                    <div className="mb-6">
                        <h3 className="font-bold text-gray-900 mb-1">CFR 21 Part 11 Compliance</h3>
                        <p className="text-sm text-gray-600">
                            You are signing: <span className="font-semibold text-blue-600 font-mono">{documentTitle}</span>
                        </p>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
                        <p className="text-xs text-slate-700 leading-relaxed italic">
                            "By electronically signing this document, I certify that I have reviewed the contents and that,
                            to the best of my knowledge, the information provided is accurate and complete in accordance
                            with enterprise quality standards (IATF 16949 / ISO 9001)."
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
                                Full Name (Electronic Signature)
                            </label>
                            <input
                                type="text"
                                value={typedName}
                                onChange={(e) => setTypedName(e.target.value)}
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 font-serif italic text-lg text-gray-900"
                                placeholder="Type your full name to sign"
                            />
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer group">
                            <div className="mt-1">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={acknowledged}
                                    onChange={() => setAcknowledged(!acknowledged)}
                                />
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${acknowledged ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-gray-300 group-hover:border-emerald-400'
                                    }`}>
                                    {acknowledged && <Check className="w-3.5 h-3.5 text-white" />}
                                </div>
                            </div>
                            <span className="text-xs text-gray-600 font-medium">
                                I acknowledge that this electronic signature is the legally binding equivalent of my handwritten signature.
                            </span>
                        </label>

                        {error && (
                            <div className="p-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2 text-xs text-red-600 font-semibold italic">
                                <AlertCircle className="w-4 h-4" />
                                {error}
                            </div>
                        )}

                        <button
                            onClick={handleSign}
                            className="w-full py-3 bg-slate-900 text-white rounded-xl hover:bg-black transition-all font-bold flex items-center justify-center gap-2 shadow-lg shadow-gray-200"
                        >
                            <ShieldCheck className="w-5 h-5 text-emerald-400" />
                            Sign & Approve Document
                        </button>
                    </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[10px] text-gray-400 font-medium uppercase tracking-tighter">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                        Secure TLS Link Active
                    </div>
                    <span className="text-[10px] text-gray-400 font-mono">ID: {user?.uid.substring(0, 8)}...</span>
                </div>
            </div>
        </div>
    );
};
